import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { Feedback, FeedbackStatus, FeedbackType } from '@brafton/feedback-api';
import { StatusChip } from '@brafton/feedback-components';

/** A feedback row's status change, bubbled up so the host can persist it. */
export interface FeedbackStatusChange {
  id: string;
  status: string;
}

/** A feedback item projected into the compact row shape the panel list renders. */
interface FeedbackRow {
  id: string;
  number: number;
  title: string;
  status: string;
  device: string;
  deviceLabel: string;
  timeAgo: string;
  type: 'User' | 'AI' | null;
  /** Unread comments on this item for the current reporter. */
  unread: number;
}

/**
 * Paginated, read-only list of feedback for the current page. Renders the design's
 * panel rows (pin number, title, status pill, device + time + source). Row clicks
 * and status edits are intentionally NOT wired up yet — display only. The pager
 * emits `pageChange`; the host fetches the requested page.
 */
@Component({
  selector: 'app-feedback-list',
  templateUrl: './feedback-list.html',
  styleUrls: ['./feedback-list.scss', './feedback-list.premium.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [StatusChip],
})
export class FeedbackList {
  /** The current page of feedback items. */
  readonly items = input<Feedback[]>([]);

  /**
   * Unread comment counts for the current reporter, keyed by feedback id. A row with a
   * count shows a badge; a missing key counts as zero.
   */
  readonly unreadCounts = input<Record<string, number>>({});

  /** Pagination state (from the API's paginated response). */
  readonly page = input(1);
  readonly perPage = input(0);
  readonly total = input(0);
  readonly lastPage = input(1);

  /** Emits the page number to load when the reporter pages through the list. */
  readonly pageChange = output<number>();

  /** Emits when a row's status is changed via its chip. */
  readonly statusChange = output<FeedbackStatusChange>();

  /** Emits the feedback item when a row is clicked (to open its detail popover). */
  readonly openFeedback = output<Feedback>();

  /** The statuses offered in each row's status chip. */
  protected readonly statuses = Object.values(FeedbackStatus);

  protected onStatusChange(row: FeedbackRow, status: string): void {
    this.statusChange.emit({ id: row.id, status });
  }

  protected onRowClick(row: FeedbackRow): void {
    const item = this.items().find((feedback) => feedback.id === row.id);
    if (item) {
      this.openFeedback.emit(item);
    }
  }

  /** The items projected into row view-models. */
  protected readonly rows = computed<FeedbackRow[]>(() => this.items().map((item) => this.toRow(item)));

  /** Whether the pager should show (more than a single page of results). */
  protected readonly showPager = computed(() => this.total() > this.perPage() && this.perPage() > 0);

  /** Page numbers for the pager. */
  protected readonly pageNumbers = computed(() =>
    Array.from({ length: Math.max(1, this.lastPage()) }, (_unused, index) => index + 1),
  );

  /** 1-based index of the first item shown on the current page. */
  protected readonly rangeStart = computed(() => (this.page() - 1) * this.perPage() + 1);

  /** 1-based index of the last item shown on the current page. */
  protected readonly rangeEnd = computed(() => Math.min(this.total(), this.page() * this.perPage()));

  protected goTo(page: number): void {
    if (page >= 1 && page <= this.lastPage() && page !== this.page()) {
      this.pageChange.emit(page);
    }
  }

  private toRow(item: Feedback): FeedbackRow {
    const meta = (item.meta ?? {}) as Record<string, unknown>;
    const marker = (item.marker ?? {}) as Record<string, unknown>;
    const number = Number(marker['id']) || 0;
    const device = typeof meta['screen'] === 'string' ? (meta['screen'] as string) : 'desktop';
    return {
      id: item.id,
      number,
      title: item.element || `Untitled element #${number}`,
      status: item.status,
      device,
      deviceLabel: device.charAt(0).toUpperCase() + device.slice(1),
      timeAgo: this.timeAgo(item.createdAt),
      type: item.type === FeedbackType.AiFeedback ? 'AI' : item.type === FeedbackType.UserFeedback ? 'User' : null,
      unread: this.unreadCounts()[item.id] ?? 0,
    };
  }

  /** A short "x ago" string from an ISO timestamp. */
  private timeAgo(iso?: string): string {
    if (!iso) {
      return '';
    }
    const then = Date.parse(iso);
    if (Number.isNaN(then)) {
      return '';
    }
    const seconds = Math.floor((Date.now() - then) / 1000);
    if (seconds < 60) {
      return 'just now';
    }
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) {
      return `${minutes}m ago`;
    }
    const hours = Math.floor(minutes / 60);
    if (hours < 24) {
      return `${hours}h ago`;
    }
    return `${Math.floor(hours / 24)}d ago`;
  }
}
